import React, { useMemo } from 'react'
import { Typography } from 'antd'

const { Title } = Typography

const defaultColors = [
    '#ffffff',
    '#ffd84a',
    '#0b73b7',
    '#d9363e',
    '#80c6a0',
    '#59bed1',
    '#aa92df',
    '#086faf',
]

const defaultBenefitItems = [
    { icon: '🎁', text: 'Prêmios incríveis' },
    { icon: '✨', text: '100% gratuito' },
    { icon: '🏆', text: 'Rápido e fácil' },
]

const modelsWithRoundLights = [
    'MODEL_1',
    'MODEL_2',
    'MODEL_4',
    'MODEL_5',
]

const modelsWithLedBars = [
    'MODEL_6',
]

const getImageUrl = (
    url,
    apiBaseUrl,
) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith('http')
    ) {
        return url
    }

    return `${apiBaseUrl}${url}`
}

const isLightColor = (color) => {
    if (
        !color ||
        !color.startsWith('#')
    ) {
        return false
    }

    const hex =
        color.replace('#', '')

    const normalizedHex =
        hex.length === 3
            ? hex
                .split('')
                .map(
                    (char) =>
                        `${char}${char}`,
                )
                .join('')
            : hex

    if (
        normalizedHex.length !== 6
    ) {
        return false
    }

    const r = parseInt(
        normalizedHex.substring(
            0,
            2,
        ),
        16,
    )

    const g = parseInt(
        normalizedHex.substring(
            2,
            4,
        ),
        16,
    )

    const b = parseInt(
        normalizedHex.substring(
            4,
            6,
        ),
        16,
    )

    if (
        [r, g, b].some(
            (value) =>
                Number.isNaN(value),
        )
    ) {
        return false
    }

    return (
        r * 0.299 +
        g * 0.587 +
        b * 0.114 >
        175
    )
}

const formatPrizeName = (
    name,
) => {
    const text = String(
        name || '',
    )
        .trim()
        .replace(/\s+/g, ' ')

    if (!text) {
        return []
    }

    const words =
        text.split(' ')

    if (
        words.length === 1
    ) {
        return [
            words[0],
        ]
    }

    const maxCharactersPerLine =
        13

    const lines = []

    let currentLine = ''

    words.forEach(
        (word) => {
            const nextLine =
                currentLine
                    ? `${currentLine} ${word}`
                    : word

            if (
                nextLine.length <=
                maxCharactersPerLine ||
                !currentLine
            ) {
                currentLine =
                    nextLine

                return
            }

            lines.push(
                currentLine,
            )

            currentLine =
                word
        },
    )

    if (currentLine) {
        lines.push(
            currentLine,
        )
    }

    if (
        lines.length > 3
    ) {
        return [
            lines[0],
            lines[1],
            lines
                .slice(2)
                .join(' '),
        ]
    }

    return lines
}

const getPrizeTextStyle = (
    name,
    wheelSize,
    prizeCount,
) => {
    const lines =
        formatPrizeName(name)

    const longestLine =
        lines.reduce(
            (
                largest,
                line,
            ) =>
                Math.max(
                    largest,
                    line.length,
                ),
            0,
        )

    let fontSize =
        wheelSize * 0.027

    if (
        prizeCount >= 10
    ) {
        fontSize =
            wheelSize * 0.024
    }

    if (
        longestLine > 11
    ) {
        fontSize *= 0.9
    }

    if (
        longestLine > 15
    ) {
        fontSize *= 0.82
    }

    if (
        lines.length >= 3
    ) {
        fontSize *= 0.88
    }

    return {
        fontSize: `${Math.max(
            12,
            Math.min(
                fontSize,
                21,
            ),
        )}px`,

        lineHeight:
            lines.length >= 3
                ? 0.98
                : 1.05,

        width:
            `${wheelSize * 0.23}px`,

        maxWidth:
            `${wheelSize * 0.23}px`,
    }
}

const getPageClassName = (
    game,
) => {
    const orientation = (
        game?.layoutOrientation ||
        'AUTO'
    ).toLowerCase()

    const mode = (
        game?.layoutMode ||
        'CENTER'
    ).toLowerCase()

    return (
        `play-page ` +
        `layout-${orientation} ` +
        `mode-${mode}`
    )
}

const getPageStyle = (
    game,
    apiBaseUrl,
) => ({
    backgroundColor:
        game?.backgroundColor ||
        '#13062f',

    backgroundImage:
        game?.backgroundUrl
            ? `url(${getImageUrl(
                game.backgroundUrl,
                apiBaseUrl,
            )})`
            : undefined,

    color:
        game?.textColor ||
        '#ffffff',
})

const getWheelPositionClass = (
    game,
) => {
    if (
        game?.wheelPosition ===
        'TOP'
    ) {
        return (
            'wheel-position-top'
        )
    }

    if (
        game?.wheelPosition ===
        'BOTTOM'
    ) {
        return (
            'wheel-position-bottom'
        )
    }

    return (
        'wheel-position-center'
    )
}

const getWheelModelClass = (
    game,
) => {
    const model = (
        game?.wheelModel ||
        'MODEL_1'
    )
        .toLowerCase()
        .replace(
            '_',
            '-',
        )

    return `wheel-${model}`
}

const getLogoClassName = (
    game,
) => {
    const vertical = (
        game?.logoVerticalPosition ||
        'TOP'
    ).toLowerCase()

    const horizontal = (
        game?.logoHorizontalPosition ||
        'LEFT'
    ).toLowerCase()

    return (
        `play-logo ` +
        `logo-${vertical} ` +
        `logo-${horizontal}`
    )
}

const RouletteGame = ({
    game,
    prizes = [],
    loading = false,
    spinning = false,
    rotation = 0,
    showCenterLogo = false,
    onSpin,
    apiBaseUrl,
}) => {
    const wheelSize =
        Number(
            game?.wheelSize ||
            680,
        )

    const wheelBackground =
        useMemo(() => {
            if (
                !prizes.length
            ) {
                return '#ddd'
            }

            const size =
                360 /
                prizes.length

            return `conic-gradient(${prizes
                .map(
                    (
                        prize,
                        index,
                    ) => {
                        const start =
                            index * size

                        const end =
                            start + size

                        const color =
                            prize.color ||
                            prize.backgroundColor ||
                            defaultColors[
                            index %
                            defaultColors.length
                            ]

                        return (
                            `${color} ` +
                            `${start}deg ` +
                            `${end}deg`
                        )
                    },
                )
                .join(',')})`
        }, [prizes])

    return (
        <div
            className={
                getPageClassName(
                    game,
                )
            }
            style={
                getPageStyle(
                    game,
                    apiBaseUrl,
                )
            }
        >
            <div className="play-shell">
                {game.showLogo !==
                    false && (
                        <div
                            className={
                                getLogoClassName(
                                    game,
                                )
                            }
                        >
                            {game.logoUrl ? (
                                <img
                                    src={getImageUrl(
                                        game.logoUrl,
                                        apiBaseUrl,
                                    )}
                                    alt="Logo"
                                    style={{
                                        maxWidth:
                                            Number(
                                                game.logoSize ||
                                                130,
                                            ),

                                        maxHeight:
                                            Number(
                                                game.logoSize ||
                                                130,
                                            ),

                                        objectFit:
                                            'contain',
                                    }}
                                />
                            ) : (
                                <>
                                    <div
                                        style={{
                                            fontSize: 34,
                                        }}
                                    >
                                        ♕
                                    </div>

                                    <div>
                                        SUA LOGO
                                        <br />
                                        AQUI
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                <div className="play-header">
                    {game.showTitle !==
                        false && (
                            <Title
                                className="play-title"
                                style={{
                                    color:
                                        game.titleColor ||
                                        game.textColor ||
                                        '#ffffff',

                                    fontSize:
                                        Number(
                                            game.titleFontSize ||
                                            48,
                                        ),

                                    fontWeight:
                                        game.titleFontWeight ||
                                        '950',
                                }}
                            >
                                {game.title ||
                                    'GIRE & GANHE'}
                            </Title>
                        )}

                    {game.showSubtitle !==
                        false && (
                            <div
                                className="play-subtitle"
                                style={{
                                    color:
                                        game.subtitleColor ||
                                        game.textColor ||
                                        '#ffffff',

                                    fontSize:
                                        Number(
                                            game.subtitleFontSize ||
                                            20,
                                        ),

                                    fontWeight:
                                        game.subtitleFontWeight ||
                                        '700',
                                }}
                            >
                                {game.subtitle ||
                                    game.event?.name ||
                                    'A sorte pode ser sua!'}
                            </div>
                        )}
                </div>

                <div
                    className={
                        `wheel-area ` +
                        `${getWheelPositionClass(
                            game,
                        )}`
                    }
                >
                    <div
                        className={
                            `wheel-wrap ` +
                            `${getWheelModelClass(
                                game,
                            )}`
                        }
                        style={{
                            width:
                                wheelSize,

                            height:
                                wheelSize,
                        }}
                    >
                        {modelsWithRoundLights.includes(
                            game.wheelModel ||
                            'MODEL_1',
                        ) && (
                                <div className="wheel-lights">
                                    {Array.from({
                                        length: 36,
                                    }).map(
                                        (
                                            _,
                                            index,
                                        ) => {
                                            const angle =
                                                (index /
                                                    36) *
                                                Math.PI *
                                                2

                                            const radius =
                                                44.5

                                            return (
                                                <span
                                                    key={
                                                        index
                                                    }
                                                    className="wheel-light"
                                                    style={{
                                                        left:
                                                            `${50 +
                                                            Math.sin(
                                                                angle,
                                                            ) *
                                                            radius
                                                            }%`,

                                                        top:
                                                            `${50 -
                                                            Math.cos(
                                                                angle,
                                                            ) *
                                                            radius
                                                            }%`,

                                                        transform:
                                                            'translate(-50%, -50%)',
                                                    }}
                                                />
                                            )
                                        },
                                    )}
                                </div>
                            )}

                        {modelsWithLedBars.includes(
                            game.wheelModel ||
                            'MODEL_1',
                        ) && (
                                <div className="wheel-leds">
                                    {Array.from({
                                        length: 12,
                                    }).map(
                                        (
                                            _,
                                            index,
                                        ) => (
                                            <span
                                                key={
                                                    index
                                                }
                                                className="wheel-led-bar"
                                                style={{
                                                    transform:
                                                        `rotate(${(360 /
                                                            12) *
                                                        index
                                                        }deg) ` +
                                                        `translateY(-${wheelSize /
                                                        2 -
                                                        12
                                                        }px)`,
                                                }}
                                            />
                                        ),
                                    )}
                                </div>
                            )}

                        <div className="wheel-pointer" />

                        <div
                            className={
                                `wheel ` +
                                `${getWheelModelClass(
                                    game,
                                )} ` +
                                `${spinning
                                    ? 'wheel-spinning'
                                    : ''
                                }`
                            }
                            style={{
                                background:
                                    wheelBackground,

                                transform:
                                    `rotate(${rotation}deg)`,

                                transition:
                                    spinning
                                        ? `transform ${Number(
                                            game.spinDuration ||
                                            5000,
                                        )}ms cubic-bezier(.12,.76,.21,1)`
                                        : 'none',
                            }}
                        >
                            {prizes.map(
                                (
                                    prize,
                                    index,
                                ) => {
                                    const angle =
                                        (360 /
                                            prizes.length) *
                                        index +
                                        360 /
                                        prizes.length /
                                        2

                                    const distance =
                                        prizes.length >=
                                            10
                                            ? wheelSize *
                                            0.285
                                            : wheelSize *
                                            0.3

                                    const segmentColor =
                                        prize.color ||
                                        prize.backgroundColor ||
                                        defaultColors[
                                        index %
                                        defaultColors.length
                                        ]

                                    const textDark =
                                        isLightColor(
                                            segmentColor,
                                        )

                                    const prizeNameLines =
                                        formatPrizeName(
                                            prize.name,
                                        )

                                    return (
                                        <div
                                            key={
                                                prize.id ||
                                                index
                                            }
                                            className="wheel-segment-content"
                                            style={{
                                                transform:
                                                    `rotate(${angle}deg) ` +
                                                    `translateY(-${distance}px)`,
                                            }}
                                        >
                                            {prize.imageUrl ? (
                                                <img
                                                    className="prize-img prize-img-only"
                                                    src={getImageUrl(
                                                        prize.imageUrl,
                                                        apiBaseUrl,
                                                    )}
                                                    alt={
                                                        prize.name
                                                    }
                                                />
                                            ) : (
                                                <div
                                                    className={
                                                        `prize-text ${textDark
                                                            ? 'dark'
                                                            : ''
                                                        }`
                                                    }
                                                    style={
                                                        getPrizeTextStyle(
                                                            prize.name,
                                                            wheelSize,
                                                            prizes.length,
                                                        )
                                                    }
                                                >
                                                    {prizeNameLines.map(
                                                        (
                                                            line,
                                                            lineIndex,
                                                        ) => (
                                                            <span
                                                                key={`${prize.id || index}-${lineIndex}`}
                                                                className="prize-text-line"
                                                            >
                                                                {
                                                                    line
                                                                }
                                                            </span>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )
                                },
                            )}
                        </div>

                        <button
                            type="button"
                            className="wheel-center"
                            onClick={
                                onSpin
                            }
                            disabled={
                                loading ||
                                spinning ||
                                !prizes.length
                            }
                            style={
                                game.wheelModel ===
                                    'MODEL_7'
                                    ? {}
                                    : {
                                        background:
                                            `linear-gradient(180deg, ${game.primaryColor ||
                                            '#7c3aed'
                                            }, ${game.secondaryColor ||
                                            '#371169'
                                            })`,
                                    }
                            }
                        >
                            {game.wheelModel ===
                                'MODEL_7' && (
                                    <img
                                        className="wheel-center-button"
                                        src="/images/wheels/model7/center-button.png"
                                        alt=""
                                    />
                                )}

                            {showCenterLogo &&
                                game.logoUrl ? (
                                <img
                                    className="wheel-center-logo"
                                    src={getImageUrl(
                                        game.logoUrl,
                                        apiBaseUrl,
                                    )}
                                    alt="Logo"
                                />
                            ) : (
                                <span className="wheel-center-text">
                                    {game.buttonText ||
                                        'GIRAR ROLETA'}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {game.showPlayHint !==
                    false && (
                        <div
                            className="play-hint"
                            style={{
                                color:
                                    game.playHintColor ||
                                    game.textColor ||
                                    '#ffffff',

                                fontSize:
                                    Number(
                                        game.playHintFontSize ||
                                        18,
                                    ),

                                fontWeight:
                                    game.playHintFontWeight ||
                                    '800',
                            }}
                        >
                            {game.playHintText ||
                                'Clique em GIRAR para tentar a sorte!'}
                        </div>
                    )}

                {game.showTopBenefits !==
                    false && (
                        <div
                            className="play-benefits"
                            style={{
                                background:
                                    game.topBenefitsBackground ||
                                    'rgba(20, 8, 54, 0.65)',

                                borderColor:
                                    game.topBenefitsBorderColor ||
                                    'rgba(173, 92, 255, 0.75)',

                                color:
                                    game.topBenefitsTextColor ||
                                    '#ffffff',

                                borderRadius:
                                    Number(
                                        game.topBenefitsBorderRadius ||
                                        18,
                                    ),

                                padding:
                                    Number(
                                        game.topBenefitsPadding ||
                                        16,
                                    ),

                                gap:
                                    Number(
                                        game.topBenefitsGap ||
                                        24,
                                    ),
                            }}
                        >
                            {game.topBenefitsTitle && (
                                <div className="play-benefits-title">
                                    {
                                        game.topBenefitsTitle
                                    }
                                </div>
                            )}

                            {(Array.isArray(
                                game.topBenefitsItems,
                            )
                                ? game.topBenefitsItems
                                : defaultBenefitItems
                            ).map(
                                (
                                    item,
                                    index,
                                ) => (
                                    <div
                                        key={
                                            index
                                        }
                                        className="play-benefit"
                                    >
                                        <span>
                                            {item.icon}
                                        </span>

                                        <span>
                                            {item.text}
                                        </span>
                                    </div>
                                ),
                            )}
                        </div>
                    )}

                {game.showBottomBanner && (
                    <div
                        className="play-bottom-banner"
                        style={{
                            background:
                                game.bottomBannerBackground ||
                                'rgba(20, 8, 54, 0.65)',

                            borderColor:
                                game.bottomBannerBorderColor ||
                                'rgba(173, 92, 255, 0.75)',

                            color:
                                game.bottomBannerTextColor ||
                                '#ffffff',

                            borderRadius:
                                Number(
                                    game.bottomBannerBorderRadius ||
                                    18,
                                ),

                            padding:
                                Number(
                                    game.bottomBannerPadding ||
                                    16,
                                ),
                        }}
                    >
                        <div className="play-bottom-banner-icon">
                            {game.bottomBannerIcon ||
                                '🛡️'}
                        </div>

                        <div>
                            <div className="play-bottom-banner-title">
                                {game.bottomBannerTitle ||
                                    'Seus dados estão protegidos com segurança'}
                            </div>

                            <div className="play-bottom-banner-subtitle">
                                {game.bottomBannerSubtitle ||
                                    'Participação 100% segura e dentro da LGPD'}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default RouletteGame